(() => {
  const root = document.querySelector('[data-mishop-ai]');
  if (!root) return;
  const launcher = root.querySelector('.mishop-ai__launcher');
  const panel = root.querySelector('.mishop-ai__panel');
  const close = root.querySelector('.mishop-ai__close');
  const messages = root.querySelector('.mishop-ai__messages');
  const form = root.querySelector('.mishop-ai__composer');
  const input = form.querySelector('textarea');
  const endpoint = (root.dataset.endpoint || '').trim().replace(/\/$/, '');
  const merchantId = Number(root.dataset.merchantId || 1);
  const whatsapp = (root.dataset.whatsapp || '').replace(/\D/g, '');
  const shopName = String(root.dataset.shopName || document.documentElement.dataset.shopName || 'la tienda').trim();
  const storeKey = String(root.dataset.storeKey || location.hostname).trim().toLowerCase();
  const uiStateKey = `commerce:${storeKey}:ai-ui-session:v1`;
  const legacyStorageKey = 'mishop-ai-conversation-v1';
  const storageKey = `mishop-ai-conversation-v2-${merchantId}`;
  const transcriptKey = `mishop-ai-transcript-v1-${merchantId}`;
  const transcriptMaxMessages = 80;
  const transcriptMaxAgeMs = 30 * 24 * 60 * 60 * 1000;
  const variantData = document.querySelector('[data-mishop-product-variants]');
  let productVariants = [];
  try { productVariants = variantData ? JSON.parse(variantData.textContent || '[]') : []; } catch (error) { productVariants = []; }
  const welcomeMessage = '¡Hola! Te ayudo a encontrar productos para tu negocio. ¿Qué buscas y cuántas piezas necesitas?';
  const storage = {
    get(key) { try { return localStorage.getItem(key); } catch (error) { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); return true; } catch (error) { return false; } },
    remove(key) { try { localStorage.removeItem(key); } catch (error) {} }
  };
  const session = {
    get() { try { return sessionStorage.getItem(uiStateKey); } catch (error) { return null; } },
    set(value) { try { sessionStorage.setItem(uiStateKey, value); } catch (error) {} },
    removeLegacy() { try { sessionStorage.removeItem('mishop-ai-open'); } catch (error) {} }
  };
  function validConversationId(value) {
    const normalized = String(value || '').trim();
    return /^[a-zA-Z0-9-]{1,100}$/.test(normalized) ? normalized : '';
  }
  const migratedConversationId = validConversationId(storage.get(storageKey) || storage.get(legacyStorageKey));
  let conversationId = migratedConversationId;
  if (conversationId) storage.set(storageKey, conversationId);
  storage.remove(legacyStorageKey);
  let busy = false;
  let quoteContext = null;
  let guidedFlow = null;
  const defaultPlaceholder = input.getAttribute('placeholder') || 'Escribe tu consulta';
  const isSafeEndpoint = () => /^https:\/\//i.test(endpoint);

  function track(name, payload = {}, once = false) {
    document.dispatchEvent(new CustomEvent('mishop:conversion', { detail: { name, payload, once } }));
  }

  function ensureConversationId() {
    if (!conversationId) {
      conversationId = typeof crypto?.randomUUID === 'function'
        ? crypto.randomUUID()
        : `mishop-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      storage.set(storageKey, conversationId);
    }
    return conversationId;
  }

  function readLocalTranscript() {
    try {
      const value = JSON.parse(storage.get(transcriptKey) || 'null');
      if (!value || value.version !== 1 || value.conversationId !== conversationId || !Array.isArray(value.messages)) return null;
      if (!Number.isFinite(value.updatedAt) || Date.now() - value.updatedAt > transcriptMaxAgeMs) {
        storage.remove(transcriptKey);
        return null;
      }
      return value.messages.slice(-transcriptMaxMessages).filter(item =>
        item && (item.sender === 'customer' || item.sender === 'system') && typeof item.content === 'string'
      );
    } catch (error) {
      storage.remove(transcriptKey);
      return null;
    }
  }

  function writeLocalTranscript(items) {
    if (!conversationId) return;
    const safeItems = items.slice(-transcriptMaxMessages).map(item => ({
      sender: item.sender === 'customer' ? 'customer' : 'system',
      content: String(item.content || '').slice(0, 4000),
      responseType: String(item.responseType || '').slice(0, 80),
      createdAt: item.createdAt || new Date().toISOString()
    }));
    storage.set(transcriptKey, JSON.stringify({ version: 1, conversationId, updatedAt: Date.now(), messages: safeItems }));
  }

  function currentTranscript() {
    return [...messages.querySelectorAll('.mishop-ai__message[data-history="1"]')].map(item => ({
      sender: item.dataset.sender === 'customer' ? 'customer' : 'system',
      content: item.dataset.content || item.textContent || '',
      responseType: item.dataset.responseType || '',
      createdAt: item.dataset.createdAt || new Date().toISOString()
    }));
  }

  function persistTranscript() { writeLocalTranscript(currentTranscript()); }
  function clearRenderedMessages() { messages.replaceChildren(); }

  function positiveQuantity(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  function getCurrentProductContext(overrides = {}) {
    const productId = String(overrides.product_id || root.dataset.productId || '').trim();
    const productInfo = productId
      ? document.querySelector(`product-info[data-product-id="${productId}"]`)
      : document.querySelector('product-info[data-product-id]');
    const sectionId = productInfo?.dataset.section || overrides.section_id || '';
    const quantityInput = sectionId
      ? document.getElementById(`Quantity-Form-${sectionId}`)?.querySelector('input[name="quantity"]')
      : productInfo?.querySelector('input[name="quantity"]');
    const variantInput = productInfo?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    const variantId = String(variantInput?.value || overrides.variant_id || root.dataset.productVariantId || '').trim();
    const selectedVariant = productVariants.find(variant => String(variant.id) === variantId);
    const title = String(overrides.title || root.dataset.productTitle || '').trim();
    if (!title) return null;
    const context = {
      product_id: productId,
      title,
      variant_id: variantId,
      quantity: positiveQuantity(quantityInput?.value || overrides.quantity),
      url: String(overrides.url || root.dataset.productUrl || window.location.href).split('?')[0]
    };
    const sku = String(selectedVariant?.sku || overrides.sku || root.dataset.productSku || '').trim();
    const variantTitle = selectedVariant ? String(selectedVariant.title || '').trim() : '';
    if (sku) context.sku = sku;
    if (variantTitle && variantTitle.toLowerCase() !== 'default title') context.variant_title = variantTitle;
    return context;
  }

  function quoteMessage(context) {
    if (!context) return 'Quiero una cotización de mayoreo.';
    const sku = context.sku ? ` (SKU: ${context.sku})` : '';
    return `Quiero una cotización de mayoreo de ${context.quantity} pieza(s) de ${context.title}${sku}.`;
  }

  function whatsappText(message = '', overrides = {}) {
    const context = getCurrentProductContext(overrides);
    const lines = [`Hola ${shopName}, necesito ayuda para comprar al mayoreo.`];
    if (context) {
      lines.push(`Producto: ${context.title}`);
      if (context.sku) lines.push(`SKU: ${context.sku}`);
      if (context.variant_title) lines.push(`Variante: ${context.variant_title}`);
      lines.push(`Cantidad: ${context.quantity}`);
      lines.push(`Enlace: ${context.url}`);
    } else {
      const pageTitle = String(document.title || '')
        .replace(/\s+[–—-]\s+[^–—-]+\s*$/i, '')
        .trim();
      const pageUrl = `${window.location.origin}${window.location.pathname}`;
      if (pageTitle) lines.push(`Página: ${pageTitle}`);
      lines.push(`Enlace: ${pageUrl}`);
    }
    if (message) lines.push(`Consulta: ${message}`);
    return lines.join('\n');
  }

  function whatsappUrl(message = '', overrides = {}) {
    if (!whatsapp) return '#';
    return `https://wa.me/${whatsapp}?text=${encodeURIComponent(whatsappText(message, overrides))}`;
  }

  function refreshWhatsappLinks() {
    root.querySelectorAll('[data-ai-whatsapp]').forEach(link => { link.href = whatsappUrl(); });
  }

  function visible(element) {
    if (!element) return false;
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) > 0 && rect.width > 0 && rect.height > 0;
  }

  function commerceBlockerActive() {
    if (root.dataset.pageType === 'cart' || document.body.matches('.template-cart, [data-template="cart"]')) return true;
    const checkoutSelectors = [
      'cart-drawer[open] [name="checkout"]',
      'cart-drawer.is-open [name="checkout"]',
      '.cart-drawer.active [name="checkout"]',
      '[data-cart-drawer][open] [name="checkout"]',
      'dialog[open] [name="checkout"]',
      '[role="dialog"][aria-hidden="false"] [name="checkout"]'
    ];
    return checkoutSelectors.some(selector => [...document.querySelectorAll(selector)].some(visible));
  }

  function floatingObstacles() {
    const selectors = [
      'a[href*="wa.me/"]',
      '[data-whatsapp-floating]',
      '[aria-label*="WhatsApp" i]',
      '[title*="WhatsApp" i]',
      'iframe[src*="whatsapp" i]',
      'iframe[src*="hcaptcha"]',
      'iframe[src*="captcha"]',
      '.h-captcha',
      '[data-hcaptcha-widget-id]'
    ];
    return [...new Set(selectors.flatMap(selector => [...document.querySelectorAll(selector)]))]
      .filter(element => !root.contains(element) && visible(element))
      .filter(element => getComputedStyle(element).position === 'fixed')
      .map(element => element.getBoundingClientRect())
      .filter(rect => rect.right > innerWidth - 180 && rect.bottom > innerHeight - 220);
  }

  function positionAboveFloatingActions() {
    const normalBase = innerWidth < 750 ? 86 : 88;
    const base = document.documentElement.dataset.commerceCaptchaActive === 'true' ? Math.max(normalBase, 170) : normalBase;
    const required = floatingObstacles().reduce((value, rect) => Math.max(value, innerHeight - rect.top + 14), base);
    root.style.setProperty('--commerce-ai-bottom', `${Math.ceil(required)}px`);
  }

  function shut({ manual = false, reason = '' } = {}) {
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    root.dataset.commerceAutoCollapsed = reason ? 'true' : 'false';
    if (manual || reason) session.set('collapsed');
    if (manual) launcher.focus();
  }

  function enforceCommerceSafety() {
    positionAboveFloatingActions();
    const blocked = commerceBlockerActive();
    root.dataset.commerceBlocker = blocked ? 'true' : 'false';
    if (blocked && !panel.hidden) shut({ reason: 'commerce_cta' });
    return blocked;
  }

  function open() {
    if (enforceCommerceSafety()) return;
    panel.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    root.dataset.commerceAutoCollapsed = 'false';
    refreshWhatsappLinks();
    setTimeout(() => input.focus(), 30);
    session.set('expanded');
    track('ai_opened', { source: 'ai_launcher' }, true);
  }
  function add(text, who = 'assistant', status = false, options = {}) {
    const el = document.createElement('div');
    el.className = `mishop-ai__message mishop-ai__message--${status ? 'status' : who}`;
    const content = String(text || '');
    if (!status && who === 'assistant') {
      let cursor = 0;
      for (const match of content.matchAll(/https:\/\/[^\s]+/g)) {
        el.append(document.createTextNode(content.slice(cursor, match.index)));
        const link = document.createElement('a');
        link.href = match[0]; link.textContent = match[0].includes('/products/') ? 'Ver producto' : match[0]; link.target = '_blank'; link.rel = 'noopener';
        el.append(link); cursor = match.index + match[0].length;
      }
      el.append(document.createTextNode(content.slice(cursor)));
    } else el.textContent = content;
    if (!status && options.history !== false) {
      el.dataset.history = '1';
      el.dataset.sender = who === 'customer' ? 'customer' : 'system';
      el.dataset.content = content;
      el.dataset.responseType = String(options.responseType || '');
      el.dataset.createdAt = String(options.createdAt || new Date().toISOString());
    }
    messages.appendChild(el);
    messages.scrollTop = messages.scrollHeight;
    if (!status && options.persist !== false) persistTranscript();
    return el;
  }

  function renderTranscript(items) {
    clearRenderedMessages();
    items.forEach(item => add(
      item.content,
      item.sender === 'customer' ? 'customer' : 'assistant',
      false,
      { persist: false, responseType: item.response_type || item.responseType, createdAt: item.created_at || item.createdAt }
    ));
    persistTranscript();
  }

  async function restoreConversation() {
    if (!conversationId) return false;
    const localItems = readLocalTranscript();
    let restored = false;
    if (localItems?.length) {
      renderTranscript(localItems);
      restored = true;
    }
    if (isSafeEndpoint()) {
      try {
        const url = `${endpoint}/api/chat/history?merchant_id=${encodeURIComponent(merchantId)}&conversationId=${encodeURIComponent(conversationId)}&language=es`;
        const response = await fetch(url, { signal: AbortSignal.timeout(8000), cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data.messages) && data.messages.length) {
            renderTranscript(data.messages);
            return true;
          }
        }
      } catch (error) {}
    }
    return restored;
  }

  async function resetConversation() {
    if (!window.confirm(`¿Quieres iniciar una conversación nueva? El historial anterior seguirá disponible para el equipo de ${shopName}.`)) return;
    conversationId = '';
    guidedFlow = null;
    quoteContext = null;
    input.value = '';
    input.placeholder = defaultPlaceholder;
    storage.remove(storageKey);
    storage.remove(transcriptKey);
    clearRenderedMessages();
    add(welcomeMessage, 'assistant', false, { history: false });
    input.focus();
  }
  function human(context = '', source = 'ai_handoff') {
    const product = getCurrentProductContext(quoteContext || {}) || {};
    track('ai_whatsapp_handoff_clicked', { source, product_id: product.product_id || '', product_sku: product.sku || '' });
    window.open(whatsappUrl(context, quoteContext || {}), '_blank', 'noopener');
  }
  function addHumanButton(message, label = 'Continuar por WhatsApp') { const button = document.createElement('button'); button.type = 'button'; button.className = 'mishop-ai__handoff'; button.textContent = label; button.onclick = () => human(message, 'ai_response_handoff'); messages.appendChild(button); }
  function addGuideChoices(options, onSelect) {
    const group = document.createElement('div');
    group.className = 'mishop-ai__guided';
    group.setAttribute('role', 'group');
    options.forEach(option => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = option.label;
      button.addEventListener('click', () => {
        group.querySelectorAll('button').forEach(item => { item.disabled = true; });
        onSelect(option);
      }, { once: true });
      group.appendChild(button);
    });
    messages.appendChild(group);
    messages.scrollTop = messages.scrollHeight;
  }
  function guideReply(text) { add(text, 'customer'); }
  function setGuideInput(stage, message, placeholder) {
    guidedFlow.stage = stage;
    add(message);
    input.placeholder = placeholder;
    input.focus();
  }
  function wholesaleNeedOptions(category) {
    const options = {
      'audífonos': [
        ['Para reventa', 'para reventa'],
        ['Deportivos', 'para deporte'],
        ['Para llamadas', 'para llamadas y trabajo'],
        ['Con cancelación de ruido', 'con cancelación de ruido'],
        ['No estoy seguro', 'necesito orientación']
      ],
      'cargadores': [
        ['Carga rápida', 'carga rápida'],
        ['Para iPhone', 'para iPhone'],
        ['Android / USB-C', 'para Android o USB-C'],
        ['Varios dispositivos', 'para varios dispositivos'],
        ['Surtido para reventa', 'surtido para reventa']
      ],
      'cables': [
        ['USB-C', 'USB-C'],
        ['Lightning', 'Lightning para iPhone'],
        ['Micro-USB', 'Micro-USB'],
        ['Audio / AUX', 'audio o AUX'],
        ['Surtido para reventa', 'surtido para reventa']
      ],
      'bocinas': [
        ['Portátiles', 'portátiles'],
        ['RGB / Gamer', 'RGB o gamer'],
        ['Para negocio', 'para usar en un negocio'],
        ['Económicas para reventa', 'económicas para reventa'],
        ['No estoy seguro', 'necesito orientación']
      ]
    };
    return (options[category] || [
      ['Para reventa', 'para reventa'],
      ['Para mi negocio', 'para usar en mi negocio'],
      ['Para un proyecto', 'para un proyecto'],
      ['No estoy seguro', 'necesito orientación']
    ]).map(([label, value]) => ({ label, value }));
  }
  function askWholesaleNeed() {
    guidedFlow.stage = 'need';
    add(`Para recomendarte mejor, ¿qué uso buscas para ${guidedFlow.category}?`);
    addGuideChoices(wholesaleNeedOptions(guidedFlow.category), option => {
      guidedFlow.need = option.value;
      guideReply(option.label);
      askWholesaleQuantity();
    });
  }
  function askWholesaleQuantity() {
    guidedFlow.stage = 'quantity';
    add('¿Qué cantidad aproximada necesitas? Puedes ajustarla después.');
    addGuideChoices([
      { label: '6 piezas', value: 6 },
      { label: '12 piezas', value: 12 },
      { label: '24 piezas', value: 24 },
      { label: '50 piezas', value: 50 },
      { label: 'Otra cantidad', value: 'other' }
    ], option => {
      if (option.value === 'other') {
        guideReply('Otra cantidad');
        setGuideInput('custom-quantity', 'Escribe cuántas piezas necesitas.', 'Ej. 100');
        return;
      }
      guidedFlow.quantity = option.value;
      guideReply(option.label);
      askWholesaleCity();
    });
  }
  function askWholesaleCity() {
    guidedFlow.stage = 'city';
    add('¿En qué ciudad recibirías el pedido? Esto nos ayuda a orientar el envío y la disponibilidad.');
    addGuideChoices([
      { label: 'Guadalajara', value: 'Guadalajara' },
      { label: 'Zapopan', value: 'Zapopan' },
      { label: 'Tlaquepaque', value: 'Tlaquepaque' },
      { label: 'Tonalá', value: 'Tonalá' },
      { label: 'Otra ciudad', value: 'other' }
    ], option => {
      if (option.value === 'other') {
        guideReply('Otra ciudad');
        setGuideInput('custom-city', 'Escribe la ciudad y el estado de entrega.', 'Ej. León, Guanajuato');
        return;
      }
      guidedFlow.city = option.value;
      guideReply(option.label);
      finishWholesaleFlow();
    });
  }
  function finishWholesaleFlow() {
    const summary = `Perfecto. Buscaré ${guidedFlow.category} ${guidedFlow.need}, ${guidedFlow.quantity} piezas, para ${guidedFlow.city}.`;
    const message = `Solicitud de mayoreo completa. Categoría: ${guidedFlow.category}. Uso: ${guidedFlow.need}. Cantidad: ${guidedFlow.quantity} piezas. Ciudad: ${guidedFlow.city}. Recomienda hasta 3 productos del catálogo de ${shopName}. No vuelvas a preguntar categoría, cantidad ni ciudad. Aclara que la existencia y el precio final se confirman antes de comprar.`;
    guidedFlow = null;
    input.placeholder = defaultPlaceholder;
    send(message, { displayMessage: summary, guidedWholesale: true });
  }
  function startWholesaleFlow() {
    guidedFlow = { stage: 'category', category: '', need: '', quantity: '', city: '' };
    add('¡Claro! Te ayudo a preparar una cotización para tu negocio. Primero elige una categoría:');
    addGuideChoices([
      { label: 'Audífonos', value: 'audífonos' },
      { label: 'Cargadores', value: 'cargadores' },
      { label: 'Cables', value: 'cables' },
      { label: 'Bocinas', value: 'bocinas' },
      { label: 'Otros', value: 'other' }
    ], option => {
      if (option.value === 'other') {
        guideReply('Otros productos');
        setGuideInput('custom-category', 'Escribe el producto o la categoría que buscas.', 'Ej. cámaras de vigilancia');
        return;
      }
      guidedFlow.category = option.value;
      guideReply(option.label);
      askWholesaleNeed();
    });
  }
  function handleGuidedInput(value) {
    if (!guidedFlow || !String(value || '').trim()) return false;
    const answer = String(value).trim();
    if (guidedFlow.stage === 'custom-category') {
      guidedFlow.category = answer;
      guideReply(answer);
      input.value = '';
      input.placeholder = defaultPlaceholder;
      askWholesaleNeed();
      return true;
    }
    if (guidedFlow.stage === 'custom-quantity') {
      const quantity = Number.parseInt(answer.replace(/[^0-9]/g, ''), 10);
      if (!Number.isFinite(quantity) || quantity < 1) {
        input.value = '';
        add('Escribe una cantidad válida de piezas.');
        return true;
      }
      guidedFlow.quantity = quantity;
      guideReply(`${quantity} piezas`);
      input.value = '';
      input.placeholder = defaultPlaceholder;
      askWholesaleCity();
      return true;
    }
    if (guidedFlow.stage === 'custom-city') {
      guidedFlow.city = answer;
      guideReply(answer);
      input.value = '';
      input.placeholder = defaultPlaceholder;
      finishWholesaleFlow();
      return true;
    }
    return false;
  }
  async function send(value, options = {}) {
    const message = String(value || '').trim(); if (!message || busy) return; ensureConversationId();
    const currentProduct = getCurrentProductContext(quoteContext || {}) || {};
    track('ai_question_submitted', {
      source: options.source || (options.guidedWholesale ? 'guided_wholesale' : 'typed'),
      product_id: currentProduct.product_id || '',
      product_sku: currentProduct.sku || ''
    });
    add(options.displayMessage || message, 'customer'); input.value = '';
    if (!isSafeEndpoint()) { add('El asistente está conectando. Para atenderte ahora mismo, te envío con nuestro WhatsApp oficial.'); addHumanButton(message, 'Abrir WhatsApp oficial'); return; }
    busy = true; const typing = add(`${shopName} está escribiendo…`, 'assistant', true);
    try {
      const payload = { message, conversationId, merchant_id: merchantId, language: 'es' };
      if (currentProduct) payload.current_product = currentProduct;
      const response = await fetch(`${endpoint}/api/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json; charset=utf-8', 'X-Mishop-Storefront': 'shopify' }, body: JSON.stringify(payload), signal: AbortSignal.timeout(18000) });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json(); typing.remove(); conversationId = validConversationId(data.conversationId) || conversationId; if (conversationId) storage.set(storageKey, conversationId); add(data.reply || 'Un asesor revisará tu consulta.', 'assistant', false, { responseType: data.responseType || 'ai' });
      if (options.guidedWholesale) {
        addGuideChoices([
          { label: 'Nueva cotización', value: 'new' },
          { label: 'Hablar con un asesor', value: 'advisor' }
        ], option => option.value === 'advisor' ? human(message) : startWholesaleFlow());
      }
      if (/NEEDS_BUSINESS_CONFIRMATION|HUMAN|handoff|privacy|PAYMENT/i.test(data.responseType || '')) addHumanButton(message);
    } catch (error) { typing.remove(); add('No pude conectar en este momento. Un asesor puede ayudarte ahora por WhatsApp.'); addHumanButton(message, 'Abrir WhatsApp oficial'); }
    finally { busy = false; messages.scrollTop = messages.scrollHeight; }
  }
  launcher.addEventListener('click', () => panel.hidden ? open() : shut({ manual: true })); close.addEventListener('click', () => shut({ manual: true }));
  root.querySelector('[data-ai-reset]')?.addEventListener('click', resetConversation);
  form.addEventListener('submit', e => { e.preventDefault(); if (!handleGuidedInput(input.value)) send(input.value); }); input.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); form.requestSubmit(); } });
  root.querySelectorAll('[data-ai-flow="wholesale"]').forEach(b => b.addEventListener('click', startWholesaleFlow));
  root.querySelectorAll('[data-ai-prompt]').forEach(b => b.addEventListener('click', () => { guidedFlow = null; input.placeholder = defaultPlaceholder; send(b.dataset.aiPrompt, { source: 'quick_prompt' }); })); root.querySelectorAll('[data-ai-human]').forEach(b => b.addEventListener('click', () => human('', 'ai_human_button')));
  document.addEventListener('mishop:wholesale-quote', event => {
    quoteContext = event.detail?.product || null;
    const context = getCurrentProductContext(quoteContext || {});
    open();
    input.value = event.detail?.message || quoteMessage(context);
    input.focus();
  });
  document.querySelectorAll('[data-mishop-wholesale-cta]').forEach(cta => {
    const source = cta.dataset.mishopWholesaleSource || 'product_wholesale';
    const ctaContext = () => ({
      product_id: cta.dataset.productId,
      title: cta.dataset.productTitle,
      sku: cta.dataset.productSku,
      variant_id: cta.dataset.productVariantId,
      quantity: cta.dataset.quantity || cta.closest('product-info')?.querySelector('input[name="quantity"]')?.value || 1,
      url: cta.dataset.productUrl,
      section_id: cta.dataset.sectionId
    });
    const aiButton = cta.querySelector('[data-mishop-wholesale-ai]');
    const whatsappLink = cta.querySelector('[data-mishop-wholesale-whatsapp]');
    const refreshCtaWhatsapp = () => { if (whatsappLink) whatsappLink.href = whatsappUrl('', ctaContext()); };
    aiButton?.addEventListener('click', () => {
      const context = getCurrentProductContext(ctaContext());
      track('product_wholesale_ai_clicked', {
        source,
        product_id: context?.product_id || '',
        product_sku: context?.sku || ''
      });
      document.dispatchEvent(new CustomEvent('mishop:wholesale-quote', { detail: { product: context, message: quoteMessage(context) } }));
    });
    whatsappLink?.addEventListener('pointerdown', refreshCtaWhatsapp);
    whatsappLink?.addEventListener('focus', refreshCtaWhatsapp);
    refreshCtaWhatsapp();
  });
  document.addEventListener('change', event => {
    if (event.target.matches('input[name="quantity"], input[name="id"]')) {
      refreshWhatsappLinks();
      document.querySelectorAll('[data-mishop-wholesale-whatsapp]').forEach(link => link.dispatchEvent(new Event('focus')));
    }
  });
  async function init() {
    root.hidden = false;
    session.removeLegacy();
    const restored = await restoreConversation();
    if (!restored && !messages.querySelector('.mishop-ai__message')) add(welcomeMessage, 'assistant', false, { history: false });
    refreshWhatsappLinks();
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !panel.hidden) shut({ manual: true }); });
    const safetyObserver = new MutationObserver(enforceCommerceSafety);
    safetyObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
    window.addEventListener('resize', enforceCommerceSafety, { passive: true });
    window.addEventListener('pageshow', enforceCommerceSafety);
    enforceCommerceSafety();
    if (session.get() === 'expanded' && !commerceBlockerActive()) open();
  }
  init();
})();
